import { format } from 'date-fns';
import _ from 'lodash';

import { DateHelper } from 'src/app/helpers/date.helper';
import {
  DeliveryType,
  PickupDeliveryFee,
  PickupDeliveryFeeType,
  Ticket,
  TicketSource
} from 'src/app/models';

export class TicketHelper {
  static calculateFormattedTakeoutDateTime({
    ticket_date,
    meal_delivery_area_timing_id,
    scheduled_ticket_time,
    order_source
  }: Partial<Ticket>): string {
    const formattedTicketDate = format(
      DateHelper.parse(ticket_date),
      'EEE, MMM dd, yyyy'
    );
    const scheduledTicketTime = meal_delivery_area_timing_id
      ? scheduled_ticket_time
      : 'ASAP';
    const isPosOrderSource = order_source === TicketSource.POS;

    return `${formattedTicketDate} ${
      isPosOrderSource ? '' : '| ' + scheduledTicketTime
    }`;
  }

  static initializeTicket(ticket: Ticket): Ticket {
    ticket.calculated_total = ticket.calculated_total || 0;
    ticket.mealplan_balance_used = ticket.mealplan_balance_used || 0;
    ticket.outstanding_balance = ticket.outstanding_balance || 0;
    ticket.pre_tax_subtotal = ticket.pre_tax_subtotal || 0;
    ticket.total_base_price = ticket.total_base_price || 0;
    ticket.total_discounts = ticket.total_discounts || 0;
    ticket.tax_total = ticket.tax_total || 0;
    ticket.ticket_items = ticket.ticket_items || [];
    ticket.can_be_cancelled = true;
    ticket.order_source = ticket.order_source || TicketSource.POS;
    return ticket;
  }

  static getPickupDeliveryFee(params: {
    deliveryType: DeliveryType;
    pickup_delivery_fees: PickupDeliveryFee[];
    isSsk?: boolean;
  }): PickupDeliveryFee {
    const { deliveryType, pickup_delivery_fees, isSsk } = params;
    const PICKUP_DELIVERY_FEE_MAPPER = pickup_delivery_fees.reduce(
      (mapper, fee) => {
        mapper[fee.type] = fee;
        return mapper;
      },
      {} as Record<PickupDeliveryFeeType, PickupDeliveryFee>
    );

    if (isSsk && deliveryType === DeliveryType.TO_GO) {
      return PICKUP_DELIVERY_FEE_MAPPER['pick_up'];
    }

    return PICKUP_DELIVERY_FEE_MAPPER[deliveryType];
  }

  static calculateTicketSubtotal(ticket: Ticket): number {
    const ticketPreTaxSubtotal = +(ticket.pre_tax_subtotal || 0);
    const feePreTaxSubtotal = _.sum(
      ticket.local_attributes?.ticketItemFees?.map(
        (feeItem) => feeItem.pre_tax_subtotal || 0
      )
    );
    return _.round(_.subtract(ticketPreTaxSubtotal, feePreTaxSubtotal), 4);
  }

  static calculateTicketDiscountApplied(ticket: Ticket): number {
    const ticketDiscountAmount = +ticket.total_discounts || 0;
    const feeItemDiscountAmount = _.sum(
      ticket.local_attributes?.ticketItemFees?.map(
        (feeItem) => +feeItem.discount_amount || 0
      )
    );

    return _.round(_.subtract(ticketDiscountAmount, feeItemDiscountAmount), 4);
  }
}
